// src/lib/storage-quota.ts
/**
 * Offline Storage Quota Manager
 * Evaluates device disk storage via navigator.storage.estimate()
 * Warns practitioners before long offline sessions if available quota is low.
 */

export interface StorageEstimateResult {
  quotaBytes: number;
  usageBytes: number;
  availableBytes: number;
  usedPercentage: number;
  estimatedRecordingMinutes: number;
  isLowStorage: boolean; // True if available < 50 MB or > 90% used
}

export async function checkStorageQuota(): Promise<StorageEstimateResult> {
  const defaultResult: StorageEstimateResult = {
    quotaBytes: 1024 * 1024 * 1024, // 1 GB fallback
    usageBytes: 50 * 1024 * 1024,
    availableBytes: 974 * 1024 * 1024,
    usedPercentage: 5,
    estimatedRecordingMinutes: 2000,
    isLowStorage: false,
  };

  if (typeof navigator === 'undefined' || !navigator.storage || !navigator.storage.estimate) {
    return defaultResult;
  }

  try {
    const estimate = await navigator.storage.estimate();
    const quota = estimate.quota || 1024 * 1024 * 1024;
    const usage = estimate.usage || 0;
    const available = Math.max(0, quota - usage);
    const usedPercentage = Math.round((usage / quota) * 100);

    // Audio recording estimated at ~12 MB/hour for compressed webm/opus audio stream
    const estimatedRecordingMinutes = Math.floor(available / (200 * 1024)); // ~200KB per minute

    const isLowStorage = available < 50 * 1024 * 1024 || usedPercentage > 90;

    return {
      quotaBytes: quota,
      usageBytes: usage,
      availableBytes: available,
      usedPercentage,
      estimatedRecordingMinutes,
      isLowStorage,
    };
  } catch (err) {
    console.warn('Failed to estimate storage quota:', err);
    return defaultResult;
  }
}

/**
 * Register Background Sync Tag for Offline Queue
 */
export async function requestBackgroundSync(tag: string = 'sync-chant-queue'): Promise<boolean> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    if ('sync' in registration) {
      await (registration as any).sync.register(tag);
      return true;
    }
  } catch (err) {
    console.warn('Background sync registration failed:', err);
  }
  return false;
}
